/**
 * Transaction Service 2.0
 * Clean, opinionated transaction management on Firestore
 * Single source of truth for all transaction operations
 */

import {
  collection,
  addDoc,
  getDocs,
  query,
  where,
  orderBy,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { auth, db } from '@/config/firebase';
import { sanitizeForFirestore } from '@/utils/firestoreHelpers';
import { validateTransactionInput } from '@/utils/transactionValidator';
import { getCategoryById as getOldCategoryById } from '@/constants/categoryTaxonomy';
import { findCategoryById as getUnifiedCategoryById } from '@/constants/unifiedCategories';
import { toTitleCase } from '@/lib/utils';
// CATEGORY CORE SSOT: Use single source of truth for category names
import { getCategoryDisplayName, normalizeToCategoryTaxonomyId } from '@/constants/categoryCore';

/**
 * Format category name - uses categoryCore SSOT for consistent naming
 * @param {string} categoryId 
 * @returns {string}
 */
function formatCategoryName(categoryId) {
  if (!categoryId) return 'Uncategorized';
  if (categoryId === 'uncategorized') return 'Uncategorized';
  
  // Try to get canonical name from categoryCore SSOT
  const canonicalId = normalizeToCategoryTaxonomyId(categoryId);
  if (canonicalId) {
    const canonicalName = getCategoryDisplayName(canonicalId);
    if (canonicalName && canonicalName !== 'Unknown') {
      return canonicalName;
    }
  }
  
  // Try direct lookup
  const displayName = getCategoryDisplayName(categoryId);
  if (displayName && displayName !== 'Unknown') {
    return displayName;
  }
  
  // Fallback: Convert kebab-case/snake_case to Title Case
  return toTitleCase(categoryId.replace(/[-_]/g, ' '));
}

/**
 * Get category by ID - tries unified categories first, then falls back to legacy
 * Also handles case-insensitive and kebab-case variations
 * @param {string} categoryId 
 * @returns {Object|null}
 */
function getCategoryById(categoryId) {
  if (!categoryId || categoryId === 'uncategorized') return null;
  
  // Try unified categories first (new system)
  const unifiedCategory = getUnifiedCategoryById(categoryId);
  if (unifiedCategory) return unifiedCategory;
  
  // Fall back to legacy taxonomy
  const legacyCategory = getOldCategoryById(categoryId);
  if (legacyCategory) return legacyCategory;
  
  // Try lowercase version
  const lowerId = categoryId.toLowerCase();
  const unifiedLower = getUnifiedCategoryById(lowerId);
  if (unifiedLower) return unifiedLower;
  
  const legacyLower = getOldCategoryById(lowerId);
  if (legacyLower) return legacyLower;
  
  return null;
}

/**
 * Add a new expense or income transaction for the current user.
 *
 * @param {{
 *   type: 'expense' | 'income',
 *   amount: number | string,
 *   currency?: string,
 *   categoryId?: string | null,
 *   categoryName?: string | null,
 *   description: string,
 *   notes?: string,
 *   date?: string | Date | null,
 * }} input
 */
export async function addTransaction(input) {
  const user = auth.currentUser;
  if (!user) {
    throw new Error('User not authenticated.');
  }

  const { isValid, errors, parsed } = validateTransactionInput(input);
  if (!isValid) {
    throw new Error(errors.join(' '));
  }

  // normalise date
  let dateTimestamp;
  if (parsed.date instanceof Date) {
    dateTimestamp = Timestamp.fromDate(parsed.date);
  } else if (typeof parsed.date === 'string' && parsed.date.trim() !== '') {
    dateTimestamp = Timestamp.fromDate(new Date(parsed.date));
  } else {
    // if no date provided, default to today
    const now = new Date();
    dateTimestamp = Timestamp.fromDate(now);
  }

  const docData = {
    userId: user.uid,
    type: parsed.type,
    amount: parsed.amount,
    currency: parsed.currency || 'GBP',

    categoryId: parsed.categoryId || null,
    categoryName: parsed.categoryName || null,

    description: parsed.description,
    notes: parsed.notes ? String(parsed.notes).trim() : null,

    date: dateTimestamp,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    source: 'manual',
  };

  const cleanData = sanitizeForFirestore(docData);

  const colRef = collection(db, 'users', user.uid, 'transactions');
  const docRef = await addDoc(colRef, cleanData);

  return {
    id: docRef.id,
    ...cleanData,
  };
}

/**
 * Get transactions with optional filters
 * @param {string} userId - User ID
 * @param {Object} filters - { startDate, endDate, type }
 * @returns {Promise<Array>}
 */
export async function getTransactions(userId, filters = {}) {
  if (!userId) {
    userId = auth.currentUser?.uid;
  }
  
  if (!userId) {
    throw new Error('User not authenticated');
  }

  try {
    const colRef = collection(db, 'users', userId, 'transactions');
    let q = query(colRef, orderBy('date', 'desc'));

    // Apply date filters
    if (filters.startDate) {
      const startDate = typeof filters.startDate === 'string' 
        ? Timestamp.fromDate(new Date(filters.startDate))
        : Timestamp.fromDate(filters.startDate);
      q = query(q, where('date', '>=', startDate));
    }

    if (filters.endDate) {
      const endDate = typeof filters.endDate === 'string'
        ? Timestamp.fromDate(new Date(filters.endDate))
        : Timestamp.fromDate(filters.endDate);
      q = query(q, where('date', '<=', endDate));
    }

    // Apply type filter
    if (filters.type) {
      q = query(q, where('type', '==', filters.type));
    }

    const snapshot = await getDocs(q);
    
    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        ...data,
        date: data.date?.toDate ? data.date.toDate() : data.date,
        createdAt: data.createdAt?.toDate ? data.createdAt.toDate() : data.createdAt,
        updatedAt: data.updatedAt?.toDate ? data.updatedAt.toDate() : data.updatedAt,
      };
    });
  } catch (error) {
    console.error('Error getting transactions:', error);
    throw error;
  }
}

/**
 * Get current month's transactions
 * @param {string} userId - Optional user ID
 * @returns {Promise<Array>}
 */
export async function getCurrentMonthTransactions(userId = null) {
  const uid = userId || auth.currentUser?.uid;
  if (!uid) throw new Error('User not authenticated');

  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);

  return getTransactions(uid, {
    startDate: startOfMonth,
    endDate: now
  });
}

/**
 * Calculate summary from transactions
 * @param {Array} transactions 
 * @returns {Object} { totalIncome, totalExpenses, balance }
 */
export function calculateSummary(transactions = []) {
  const summary = transactions.reduce(
    (acc, t) => {
      const entryType = (t.type || t.categoryType || t.transactionType || '').toLowerCase();
      const amount = Number(t.amount) || 0;
      
      if (entryType === 'income') {
        acc.totalIncome += amount;
      } else if (entryType === 'expense') {
        acc.totalExpenses += amount;
      }
      return acc;
    },
    { totalIncome: 0, totalExpenses: 0 }
  );

  summary.balance = summary.totalIncome - summary.totalExpenses;
  return summary;
}

/**
 * Get spending grouped by category
 * 
 * @param {Array} transactions 
 * @returns {Array} Array of { categoryId, categoryName, amount, count }
 */
export function getSpendingByCategory(transactions = []) {
  const expenses = transactions.filter((t) => {
    const entryType = (t.type || t.categoryType || t.transactionType || '').toLowerCase();
    return entryType === 'expense';
  });
  
  const categoryMap = {};
  
  expenses.forEach(t => {
    const catId = t.categoryId || 'uncategorized';
    // Look up category name from taxonomy, fallback to formatted ID
    const category = getCategoryById(catId);
    // Use category name if found, otherwise format the ID nicely
    const catName = category?.name || t.categoryName || formatCategoryName(catId);
    const amount = Number(t.amount) || 0;
    
    if (!categoryMap[catId]) {
      categoryMap[catId] = {
        categoryId: catId,
        categoryName: catName,
        amount: 0,
        count: 0
      };
    }
    
    categoryMap[catId].amount += amount;
    categoryMap[catId].count += 1;
  });
  
  return Object.values(categoryMap).sort((a, b) => b.amount - a.amount);
}

